<template>
    <section class="dp-layout">
        <section class="dp-left">
            <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
        </section>

        <section class="dp-right student-eval-import">
            <el-tabs v-model="activeTab" class="sei-tabs">
                <!-- 专项导入：表单卡片页，需可滚动，勿吃满列表页高度布局。最后更新：2026-09-16 -->
                <el-tab-pane label="专项导入" name="import" class="sei-import-pane">
                    <div class="sei-import-scroll">
                        <el-card shadow="never" class="import-card">
                            <div slot="header" class="card-header">
                                <span>导入配置</span>
                                <div>
                                    <el-button size="mini" type="primary" plain icon="el-icon-download"
                                        :loading="exportLoading" @click="handleExportTemplate">导出模板</el-button>
                                    <el-button size="mini" type="success" plain icon="el-icon-upload2"
                                        @click="triggerUpload">上传预览</el-button>
                                    <input ref="uploadInput" type="file" accept=".xls,.xlsx" style="display: none"
                                        @change="handleFileChange" />
                                </div>
                            </div>

                            <el-form :model="importForm" label-width="110px" size="small">
                                <el-form-item label="选择班级" required>
                                    <el-cascader v-model="importForm.classPaths" :options="classOptionsTree" filterable
                                        :props="classCascaderProps" collapse-tags style="width: 100%;"
                                        placeholder="最多选择 50 个班级"></el-cascader>
                                    <p class="form-hint">已选 {{ selectedClassIds.length }} 个班级（上限 50）</p>
                                </el-form-item>
                                <el-form-item label="评价项" required>
                                    <el-select v-model="importForm.itemIds" multiple filterable collapse-tags
                                        placeholder="请选择评价项（表扬/待改进），最多 30 个" style="width: 100%;"
                                        :loading="itemOptionsLoading">
                                        <el-option v-for="item in evaluationItemOptions" :key="item.Id"
                                            :label="item.Label" :value="item.Id"></el-option>
                                    </el-select>
                                    <p class="form-hint">已选 {{ importForm.itemIds.length }} 个评价项（上限 30）</p>
                                    <div v-if="selectedItemScoreHints.length" class="score-hint-box">
                                        <p class="score-hint-title">已选评价项允许分值（绝对值，填写 Excel 时参考）：</p>
                                        <div class="score-hint-list">
                                            <el-tag v-for="hint in selectedItemScoreHints" :key="hint.id" size="mini"
                                                type="info" class="score-hint-tag">
                                                {{ hint.shortLabel }}：{{ hint.rangeText }}
                                            </el-tag>
                                        </div>
                                    </div>
                                </el-form-item>
                                <el-form-item label="导入事由" required>
                                    <el-radio-group v-model="importForm.reasonType">
                                        <el-radio label="MonthlyExam">月考</el-radio>
                                        <el-radio label="WeeklyExam">周考</el-radio>
                                        <el-radio label="UnifiedExam">统考</el-radio>
                                        <el-radio label="Other">其他</el-radio>
                                    </el-radio-group>
                                </el-form-item>
                                <el-form-item v-if="importForm.reasonType === 'Other'" label="事由说明">
                                    <el-input v-model="importForm.reason" maxlength="200" placeholder="请填写具体事由"></el-input>
                                </el-form-item>
                                <el-form-item label="备注">
                                    <el-input type="textarea" v-model="importForm.remark" :rows="2" maxlength="500"></el-input>
                                </el-form-item>
                                <el-form-item label="推送家长">
                                    <el-switch v-model="importForm.pushToParent" active-text="推送" inactive-text="不推送"></el-switch>
                                    <span class="form-hint inline-hint">默认不推送，勾选后仅在导入成功后推送</span>
                                </el-form-item>
                            </el-form>
                        </el-card>

                        <el-card v-if="previewResult" ref="previewCard" shadow="never" class="preview-card">
                            <div slot="header" class="card-header">
                                <div class="preview-header-left">
                                    <span>预览结果</span>
                                    <span v-if="uploadFileName" class="upload-file-name">文件：{{ uploadFileName }}</span>
                                </div>
                                <el-tag :type="previewResult.CanConfirm ? 'success' : 'danger'" size="small">
                                    {{ previewResult.CanConfirm ? '可确认导入' : '存在错误，不可导入' }}
                                </el-tag>
                            </div>

                            <el-alert v-if="previewErrors.length" type="error" :closable="false" show-icon
                                class="preview-alert">
                                <template slot="title">
                                    共 {{ previewResult.ErrorCount || previewErrors.length }} 处错误，请修正 Excel 后重新上传预览
                                </template>
                                <div class="error-summary-list">
                                    <div v-for="(group, idx) in groupedPreviewErrors" :key="idx" class="error-summary-item">
                                        <strong>{{ group.count }} 条</strong>：{{ group.message }}
                                        <span v-if="group.samples.length" class="error-sample">
                                            （如 {{ formatErrorSample(group.samples[0]) }}）
                                        </span>
                                    </div>
                                </div>
                            </el-alert>

                            <div class="preview-stats">
                                <div class="preview-stat-item preview-stat-batch">
                                    <span class="preview-stat-label">批次号</span>
                                    <span class="preview-stat-value" :title="previewResult.TemplateBatchNo || ''">
                                        {{ previewResult.TemplateBatchNo || '—' }}
                                    </span>
                                </div>
                                <div class="preview-stat-item">
                                    <span class="preview-stat-label">班级</span>
                                    <span class="preview-stat-value">{{ previewResult.ClassCount || 0 }}</span>
                                </div>
                                <div class="preview-stat-item">
                                    <span class="preview-stat-label">学生</span>
                                    <span class="preview-stat-value">{{ previewResult.StudentCount || 0 }}</span>
                                </div>
                                <div class="preview-stat-item">
                                    <span class="preview-stat-label">有效单元格</span>
                                    <span class="preview-stat-value">{{ previewResult.ValidCellCount || 0 }}</span>
                                </div>
                                <div class="preview-stat-item" :class="{ 'is-error': (previewResult.ErrorCount || 0) > 0 }">
                                    <span class="preview-stat-label">错误数</span>
                                    <span class="preview-stat-value">{{ previewResult.ErrorCount || 0 }}</span>
                                </div>
                                <div class="preview-stat-item preview-stat-push">
                                    <span class="preview-stat-label">推送家长</span>
                                    <span class="preview-stat-value">{{ previewPushToParentText }}</span>
                                </div>
                            </div>

                            <el-collapse v-if="previewErrors.length" v-model="errorDetailExpanded" class="error-collapse">
                                <el-collapse-item title="查看全部错误明细" name="detail">
                                    <el-table :data="previewErrors" size="mini" max-height="280" style="width: 100%;">
                                        <el-table-column prop="SheetName" label="Sheet" min-width="100"></el-table-column>
                                        <el-table-column prop="RowIndex" label="行号" width="70" align="center"></el-table-column>
                                        <el-table-column prop="ColumnName" label="列" min-width="100"></el-table-column>
                                        <el-table-column prop="StudentName" label="学生" min-width="90"></el-table-column>
                                        <el-table-column prop="Message" label="错误原因" min-width="220"
                                            show-overflow-tooltip></el-table-column>
                                    </el-table>
                                </el-collapse-item>
                            </el-collapse>

                            <div v-if="previewRows.length" class="preview-table-toolbar">
                                <span>数据预览（{{ filteredPreviewRows.length }} 条）</span>
                                <el-radio-group v-model="previewRowFilter" size="mini">
                                    <el-radio-button label="all">全部</el-radio-button>
                                    <el-radio-button label="invalid">仅错误</el-radio-button>
                                    <el-radio-button label="valid">仅有效</el-radio-button>
                                </el-radio-group>
                            </div>

                            <el-table v-if="filteredPreviewRows.length" :data="filteredPreviewRows" size="mini"
                                max-height="360" style="width: 100%; margin-top: 8px;"
                                :row-class-name="previewRowClassName">
                                <el-table-column label="状态" width="72" align="center" fixed="left">
                                    <template slot-scope="scope">
                                        <el-tag :type="scope.row.IsValid === false ? 'danger' : 'success'" size="mini">
                                            {{ scope.row.IsValid === false ? '错误' : '有效' }}
                                        </el-tag>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="ClassName" label="班级" min-width="120"></el-table-column>
                                <el-table-column prop="StudentName" label="学生" min-width="90"></el-table-column>
                                <el-table-column prop="StuCode" label="学号" min-width="100"></el-table-column>
                                <el-table-column prop="ItemName" label="评价项" min-width="120"></el-table-column>
                                <el-table-column prop="Score" label="分值" width="80" align="center"></el-table-column>
                                <el-table-column prop="Error" label="错误原因" min-width="180" show-overflow-tooltip>
                                    <template slot-scope="scope">
                                        {{ scope.row.Error || '—' }}
                                    </template>
                                </el-table-column>
                            </el-table>

                            <div class="preview-actions">
                                <el-button size="small" @click="clearPreview">清除预览</el-button>
                                <el-button type="primary" :disabled="!previewResult.CanConfirm || !uploadFile"
                                    :loading="confirmLoading" @click="handleConfirmImport">确认导入</el-button>
                            </div>
                        </el-card>
                    </div>
                </el-tab-pane>

                <!-- 导入历史：列表内滚。最后更新：2026-09-16 -->
                <el-tab-pane label="导入历史" name="history" class="sei-history-pane">
                    <div class="tbl-scroll-host">
                        <el-table height="100%" :data="batchList" v-loading="batchLoading" style="width: 100%;">
                            <el-table-column prop="TemplateBatchNo" label="批次号" min-width="160"
                                show-overflow-tooltip></el-table-column>
                            <el-table-column prop="ReasonType" label="事由" width="90" align="center">
                                <template slot-scope="scope">{{ reasonTypeText(scope.row.ReasonType) }}</template>
                            </el-table-column>
                            <el-table-column prop="ClassCount" label="班级数" width="80" align="center"></el-table-column>
                            <el-table-column prop="StudentCount" label="学生数" width="80" align="center"></el-table-column>
                            <el-table-column prop="ItemCount" label="评价项数" width="90" align="center"></el-table-column>
                            <el-table-column prop="Status" label="状态" width="100" align="center">
                                <template slot-scope="scope">
                                    <el-tag :type="batchStatusTag(scope.row.Status)" size="mini">
                                        {{ batchStatusText(scope.row.Status) }}
                                    </el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column prop="OperatorName" label="操作人" width="100"></el-table-column>
                            <el-table-column label="推送家长" width="90" align="center">
                                <template slot-scope="scope">
                                    {{ scope.row.PushToParent ? '是' : '否' }}
                                </template>
                            </el-table-column>
                            <el-table-column prop="CreateTime" label="创建时间" width="170" align="center"></el-table-column>
                            <el-table-column label="操作" width="100" align="center" fixed="right">
                                <template slot-scope="scope">
                                    <el-button type="text" size="mini" @click="openBatchDetail(scope.row)">详情</el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                    <div class="pagination-container">
                        <el-pagination layout="total, prev, pager, next" :current-page="batchPage"
                            :page-size="batchPageSize" :total="batchTotal" @current-change="handleBatchPageChange">
                        </el-pagination>
                    </div>
                </el-tab-pane>
            </el-tabs>

            <el-dialog title="批次详情" :visible.sync="detailDialogVisible" width="72%" :close-on-click-modal="false">
                <div v-if="batchDetail">
                    <p class="detail-summary">
                        批次 {{ batchDetail.TemplateBatchNo || batchDetail.Batch && batchDetail.Batch.TemplateBatchNo }}，
                        状态 {{ batchStatusText((batchDetail.Batch && batchDetail.Batch.Status) || batchDetail.Status) }}
                    </p>
                    <el-table :data="batchDetailRows" size="mini" max-height="420" style="width: 100%;">
                        <el-table-column prop="ClassName" label="班级" min-width="120"></el-table-column>
                        <el-table-column prop="StudentName" label="学生" min-width="90"></el-table-column>
                        <el-table-column prop="StuCode" label="学号" min-width="100"></el-table-column>
                        <el-table-column prop="ItemName" label="评价项" min-width="120"></el-table-column>
                        <el-table-column prop="Score" label="分值" width="80" align="center"></el-table-column>
                        <el-table-column prop="ErrorMessage" label="失败原因" min-width="180"
                            show-overflow-tooltip></el-table-column>
                    </el-table>
                </div>
            </el-dialog>
        </section>
    </section>
</template>

<script>
import {
    getEvaluationDimensionSecondLevel,
    getStudentEvaluationItemListPage,
    exportStudentEvaluationSpecialImportTemplate,
    previewStudentEvaluationSpecialImport,
    confirmStudentEvaluationSpecialImport,
    getStudentEvaluationSpecialImportBatches,
    getStudentEvaluationSpecialImportBatchDetails
} from '../../api/modules/moral-education';
import { getSubDepartmentTree, downloadBlobExcel } from '../../api/modules/department';
import DepartmentPane from '../../components/DepartmentPane';
import { normalizeDeptId, treeNodeDeptId } from '../../utils/deptCascaderProps';

const MAX_CLASS_COUNT = 50;
const MAX_ITEM_COUNT = 30;

/** 学生专项加分导入。创建于 2026-08-28，最后更新：2026-09-16 */
export default {
    name: 'StudentEvaluationImport',
    components: { DepartmentPane },
    data() {
        return {
            DepartmentId: 0,
            activeTab: 'import',
            classOptionsTree: [],
            classCascaderProps: {
                multiple: true,
                checkStrictly: false,
                emitPath: true,
                value: 'value',
                label: 'label',
                children: 'children'
            },
            importForm: {
                classPaths: [],
                itemIds: [],
                reasonType: 'MonthlyExam',
                reason: '',
                remark: '',
                pushToParent: false
            },
            evaluationItemOptions: [],
            itemOptionsLoading: false,
            exportLoading: false,
            uploadFile: null,
            previewResult: null,
            previewErrors: [],
            previewRows: [],
            previewRowFilter: 'all',
            errorDetailExpanded: [],
            confirmLoading: false,
            batchList: [],
            batchLoading: false,
            batchPage: 1,
            batchPageSize: 10,
            batchTotal: 0,
            detailDialogVisible: false,
            batchDetail: null,
            batchDetailRows: []
        };
    },
    computed: {
        selectedClassIds() {
            const paths = this.importForm.classPaths || [];
            const ids = [];
            paths.forEach(path => {
                if (Array.isArray(path) && path.length) {
                    const leafId = path[path.length - 1];
                    if (leafId != null && !ids.includes(leafId)) {
                        ids.push(leafId);
                    }
                }
            });
            return ids;
        },
        evaluationItemMap() {
            const map = {};
            (this.evaluationItemOptions || []).forEach(item => {
                map[item.Id] = item;
            });
            return map;
        },
        selectedItemScoreHints() {
            return (this.importForm.itemIds || []).map(id => {
                const item = this.evaluationItemMap[id];
                if (!item) return null;
                const parts = (item.Label || '').split(' / ');
                const shortLabel = parts.length ? parts[parts.length - 1] : item.Label;
                return {
                    id,
                    shortLabel,
                    rangeText: this.formatItemScoreRange(item)
                };
            }).filter(Boolean);
        },
        previewPushToParentText() {
            if (!this.previewResult) return '—';
            const fromExcel = this.previewResult.PushToParent != null
                ? this.previewResult.PushToParent
                : this.previewResult.pushToParent;
            if (fromExcel === true || fromExcel === false) {
                return fromExcel ? '模板已开启（确认时可改）' : '模板未开启（确认时可改）';
            }
            return this.importForm.pushToParent ? '确认时将推送' : '确认时不推送';
        },
        uploadFileName() {
            return this.uploadFile ? this.uploadFile.name : '';
        },
        groupedPreviewErrors() {
            const groups = new Map();
            (this.previewErrors || []).forEach(err => {
                const message = (err && err.Message) || '未知错误';
                if (!groups.has(message)) {
                    groups.set(message, { message, count: 0, samples: [] });
                }
                const group = groups.get(message);
                group.count += 1;
                if (group.samples.length < 3) {
                    group.samples.push(err);
                }
            });
            return Array.from(groups.values()).sort((a, b) => b.count - a.count);
        },
        filteredPreviewRows() {
            const rows = this.previewRows || [];
            if (this.previewRowFilter === 'invalid') {
                return rows.filter(row => row.IsValid === false);
            }
            if (this.previewRowFilter === 'valid') {
                return rows.filter(row => row.IsValid !== false);
            }
            return rows;
        }
    },
    watch: {
        activeTab(val) {
            if (val === 'history') {
                this.loadBatches();
            }
        },
        'importForm.classPaths'() {
            this.clearPreviewIfStale();
        },
        'importForm.itemIds'() {
            this.clearPreviewIfStale();
        }
    },
    methods: {
        positiveDeptId(v) {
            const id = normalizeDeptId(v);
            return id || null;
        },
        reasonTypeText(val) {
            const map = {
                MonthlyExam: '月考',
                WeeklyExam: '周考',
                UnifiedExam: '统考',
                Other: '其他'
            };
            return map[val] || val || '—';
        },
        batchStatusText(status) {
            const map = {
                TemplateGenerated: '已生成模板',
                Imported: '已导入',
                Failed: '失败'
            };
            return map[status] || status || '—';
        },
        batchStatusTag(status) {
            if (status === 'Imported') return 'success';
            if (status === 'Failed') return 'danger';
            return 'info';
        },
        /** 与后端 StudentEvaluationScoreRuleService 展示语义一致。最后更新：2026-09-16 */
        formatItemScoreRange(item) {
            if (!item) return '—';
            const mode = String(item.ScoreMode || 'Fixed').trim();
            const scoreValue = Number(item.ScoreValue);
            if (mode === 'Fixed') {
                return `固定 ${this.formatScore(scoreValue)}`;
            }
            if (mode === 'Max') {
                return `0～${this.formatScore(scoreValue)}`;
            }
            const minScore = item.MinScore != null ? Number(item.MinScore) : 0;
            return `${this.formatScore(minScore)}～${this.formatScore(scoreValue)}`;
        },
        formatScore(val) {
            const num = Number(val);
            if (Number.isNaN(num)) return String(val);
            return Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/\.?0+$/, '');
        },
        formatErrorSample(err) {
            if (!err) return '';
            const parts = [];
            if (err.StudentName) parts.push(err.StudentName);
            if (err.ColumnName) parts.push(err.ColumnName);
            if (err.RowIndex) parts.push(`第 ${err.RowIndex} 行`);
            return parts.join(' / ') || '—';
        },
        previewRowClassName({ row }) {
            return row && row.IsValid === false ? 'preview-row-invalid' : '';
        },
        treeNodeClick(data) {
            this.DepartmentId = treeNodeDeptId(data);
            this.clearPreview();
            this.importForm.classPaths = [];
            this.importForm.itemIds = [];
            this.loadClassTree();
            this.loadEvaluationItemOptions();
            this.loadBatches();
        },
        loadClassTree() {
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId) {
                this.classOptionsTree = [];
                return;
            }
            getSubDepartmentTree({ pid: schoolId, type: 1 }).then(res => {
                if (res && res.data && res.data.success) {
                    const tree = res.data.response;
                    this.classOptionsTree = Array.isArray(tree) ? tree : (tree ? [tree] : []);
                } else {
                    this.classOptionsTree = [];
                }
            }).catch(() => {
                this.classOptionsTree = [];
            });
        },
        itemKindText(itemKind) {
            return itemKind === 'Praise' ? '表扬' : '待改进';
        },
        /** 加载本校全部评价项（表扬 + 待改进）。最后更新：2026-09-16 */
        loadEvaluationItemOptions() {
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId) {
                this.evaluationItemOptions = [];
                return;
            }
            this.itemOptionsLoading = true;
            getEvaluationDimensionSecondLevel({ departmentId: schoolId }).then(res => {
                if (!(res && res.data && res.data.success)) {
                    this.evaluationItemOptions = [];
                    this.itemOptionsLoading = false;
                    return;
                }
                const tree = res.data.response || [];
                const level2List = [];
                tree.forEach(l1 => {
                    (l1.Children || []).forEach(l2 => {
                        level2List.push({ l1Name: l1.Name, l2 });
                    });
                });
                if (!level2List.length) {
                    this.evaluationItemOptions = [];
                    this.itemOptionsLoading = false;
                    return;
                }
                Promise.all(level2List.map(entry => getStudentEvaluationItemListPage({
                    dimensionId: entry.l2.Id,
                    page: 1,
                    intPageSize: 500
                }).then(itemRes => {
                    const page = (itemRes && itemRes.data && itemRes.data.response) || {};
                    const rows = page.data || [];
                    return rows.map(item => ({
                        Id: item.Id,
                        Label: `${entry.l1Name} / ${entry.l2.Name} / ${this.itemKindText(item.ItemKind)} / ${item.Name}`,
                        ItemKind: item.ItemKind,
                        ScoreMode: item.ScoreMode,
                        ScoreValue: item.ScoreValue,
                        MinScore: item.MinScore
                    }));
                }).catch(() => []))).then(groups => {
                    this.itemOptionsLoading = false;
                    this.evaluationItemOptions = groups.reduce((acc, cur) => acc.concat(cur), []);
                });
            }).catch(() => {
                this.itemOptionsLoading = false;
                this.evaluationItemOptions = [];
            });
        },
        validateImportConfig() {
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId) {
                this.$message.warning('请先在左侧选择学校');
                return null;
            }
            if (!this.selectedClassIds.length) {
                this.$message.warning('请至少选择一个班级');
                return null;
            }
            if (this.selectedClassIds.length > MAX_CLASS_COUNT) {
                this.$message.warning(`单次最多选择 ${MAX_CLASS_COUNT} 个班级`);
                return null;
            }
            if (!this.importForm.itemIds.length) {
                this.$message.warning('请至少选择一个评价项');
                return null;
            }
            if (this.importForm.itemIds.length > MAX_ITEM_COUNT) {
                this.$message.warning(`单次最多选择 ${MAX_ITEM_COUNT} 个评价项`);
                return null;
            }
            if (this.importForm.reasonType === 'Other' && !this.importForm.reason) {
                this.$message.warning('事由为「其他」时请填写具体说明');
                return null;
            }
            return {
                SchoolDepartmentId: schoolId,
                ClassDepartmentIds: this.selectedClassIds,
                ItemIds: this.importForm.itemIds,
                ReasonType: this.importForm.reasonType,
                Reason: this.importForm.reasonType === 'Other' ? this.importForm.reason : '',
                Remark: this.importForm.remark || '',
                PushToParent: !!this.importForm.pushToParent
            };
        },
        handleExportTemplate() {
            const payload = this.validateImportConfig();
            if (!payload) return;
            this.exportLoading = true;
            exportStudentEvaluationSpecialImportTemplate(payload).then(res => {
                this.exportLoading = false;
                downloadBlobExcel(res, '学生专项加分导入模板');
                this.$message.success('模板导出成功，请填写后上传预览');
            }).catch(() => {
                this.exportLoading = false;
                this.$message.error('模板导出失败');
            });
        },
        triggerUpload() {
            if (!this.validateImportConfig()) return;
            if (this.$refs.uploadInput) {
                this.$refs.uploadInput.value = '';
                this.$refs.uploadInput.click();
            }
        },
        applyPreviewResponse(response) {
            this.previewResult = response || {};
            this.previewErrors = this.previewResult.Errors || this.previewResult.errors || [];
            this.previewRows = this.previewResult.PreviewRows || this.previewResult.previewRows || [];
            this.previewRowFilter = this.previewErrors.length ? 'invalid' : 'all';
            this.errorDetailExpanded = this.previewErrors.length ? ['detail'] : [];
            const excelPush = this.previewResult.PushToParent != null
                ? this.previewResult.PushToParent
                : this.previewResult.pushToParent;
            if (excelPush === true || excelPush === false) {
                this.importForm.pushToParent = !!excelPush;
            }
        },
        clearPreview() {
            this.previewResult = null;
            this.previewErrors = [];
            this.previewRows = [];
            this.previewRowFilter = 'all';
            this.errorDetailExpanded = [];
            this.uploadFile = null;
            if (this.$refs.uploadInput) {
                this.$refs.uploadInput.value = '';
            }
        },
        clearPreviewIfStale() {
            if (this.previewResult || this.uploadFile) {
                this.clearPreview();
            }
        },
        scrollToPreview() {
            this.$nextTick(() => {
                const card = this.$refs.previewCard;
                const el = card && card.$el ? card.$el : card;
                if (el && el.scrollIntoView) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            });
        },
        handleFileChange(e) {
            const files = e.target.files || [];
            if (!files.length) return;
            const file = files[0];
            if (!/\.(xls|xlsx)$/i.test(file.name)) {
                this.$message.error('请上传 xls 或 xlsx 文件');
                return;
            }
            if (!this.validateImportConfig()) {
                e.target.value = '';
                return;
            }
            this.uploadFile = file;
            const formData = new FormData();
            formData.append('file', file);
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (schoolId) {
                formData.append('schoolDepartmentId', schoolId);
            }
            const loading = this.$loading({ lock: true, text: '正在预览校验...' });
            previewStudentEvaluationSpecialImport(formData).then(res => {
                if (res.data && res.data.success) {
                    this.applyPreviewResponse(res.data.response || {});
                    this.scrollToPreview();
                    if (!this.previewResult.CanConfirm) {
                        this.$message.warning('预览存在错误，请修正 Excel 后重新上传');
                    } else {
                        this.$message.success('预览通过，可确认导入');
                    }
                } else {
                    this.clearPreview();
                    this.$message.error((res.data && res.data.msg) || '预览失败');
                }
            }).catch(() => {
                this.clearPreview();
                this.$message.error('预览失败');
            }).finally(() => {
                loading.close();
            });
        },
        handleConfirmImport() {
            if (!this.uploadFile || !this.previewResult || !this.previewResult.CanConfirm) return;
            const pushHint = this.importForm.pushToParent ? '，导入成功后将推送家长' : '';
            this.$confirm(`确认导入后将写入点评与德育流水${pushHint}，是否继续？`, '提示', { type: 'warning' }).then(() => {
                const formData = new FormData();
                formData.append('file', this.uploadFile);
                const schoolId = this.positiveDeptId(this.DepartmentId);
                if (schoolId) {
                    formData.append('schoolDepartmentId', schoolId);
                }
                formData.append('pushToParent', this.importForm.pushToParent ? 'true' : 'false');
                this.confirmLoading = true;
                confirmStudentEvaluationSpecialImport(formData).then(res => {
                    this.confirmLoading = false;
                    if (res.data && res.data.success) {
                        this.$message.success(res.data.msg || '导入成功');
                        this.clearPreview();
                        this.loadBatches();
                        this.activeTab = 'history';
                    } else {
                        this.$message.error((res.data && res.data.msg) || '导入失败');
                    }
                }).catch(() => {
                    this.confirmLoading = false;
                });
            }).catch(() => { });
        },
        loadBatches() {
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId) {
                this.batchList = [];
                this.batchTotal = 0;
                return;
            }
            this.batchLoading = true;
            getStudentEvaluationSpecialImportBatches({
                schoolDepartmentId: schoolId,
                page: this.batchPage,
                pageSize: this.batchPageSize
            }).then(res => {
                this.batchLoading = false;
                if (res.data && res.data.success) {
                    const page = res.data.response || {};
                    this.batchList = page.data || page.Data || [];
                    this.batchTotal = page.dataCount || page.DataCount || 0;
                } else {
                    this.batchList = [];
                    this.batchTotal = 0;
                }
            }).catch(() => {
                this.batchLoading = false;
                this.batchList = [];
            });
        },
        handleBatchPageChange(page) {
            this.batchPage = page;
            this.loadBatches();
        },
        openBatchDetail(row) {
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId || !row || !row.Id) return;
            getStudentEvaluationSpecialImportBatchDetails({
                schoolDepartmentId: schoolId,
                batchId: row.Id
            }).then(res => {
                if (res.data && res.data.success) {
                    this.batchDetail = res.data.response || {};
                    this.batchDetailRows = this.batchDetail.Details || this.batchDetail.details
                        || this.batchDetail.Rows || this.batchDetail.rows || [];
                    if (this.batchDetail.Batch && this.batchDetail.Batch.ErrorMessage) {
                        this.$message.warning(this.batchDetail.Batch.ErrorMessage);
                    }
                    this.detailDialogVisible = true;
                } else {
                    this.$message.error((res.data && res.data.msg) || '加载详情失败');
                }
            });
        }
    }
};
</script>

<style scoped>
/* 本页同时含「表单导入」与「历史列表」两 Tab，需覆盖全局列表 Tab 高度规则。最后更新：2026-09-16 */
.student-eval-import {
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.student-eval-import >>> .sei-tabs {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.student-eval-import >>> .sei-tabs > .el-tabs__header {
    flex-shrink: 0;
}

.student-eval-import >>> .sei-tabs > .el-tabs__content {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.student-eval-import >>> .sei-tabs .el-tab-pane.sei-import-pane {
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.sei-import-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding-right: 2px;
}

.student-eval-import >>> .sei-tabs .el-tab-pane.sei-history-pane {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.student-eval-import >>> .sei-tabs .el-tab-pane.sei-history-pane > .tbl-scroll-host {
    flex: 1 1 auto;
    min-height: 0;
    height: 0;
    overflow: hidden;
}

.import-card,
.preview-card {
    margin-bottom: 16px;
}

.preview-card {
    /* 内容自适应，避免被全局 flex 撑出大片空白 */
    flex: 0 0 auto;
}

.import-card >>> .el-card__body,
.preview-card >>> .el-card__body {
    padding: 16px 20px 18px;
}

.card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.preview-header-left {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}

.upload-file-name {
    color: #909399;
    font-size: 12px;
    font-weight: normal;
}

.form-hint {
    margin: 6px 0 0;
    color: #909399;
    font-size: 12px;
}

.inline-hint {
    margin-left: 12px;
}

.score-hint-box {
    margin-top: 8px;
    padding: 8px 10px;
    background: #f4f4f5;
    border-radius: 4px;
}

.score-hint-title {
    margin: 0 0 6px;
    color: #606266;
    font-size: 12px;
}

.score-hint-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.score-hint-tag {
    max-width: 100%;
    white-space: normal;
    height: auto;
    line-height: 1.4;
}

.preview-alert {
    margin-bottom: 12px;
}

.error-summary-list {
    margin-top: 4px;
}

.error-summary-item {
    line-height: 1.6;
    font-size: 13px;
}

.error-sample {
    color: #909399;
}

.preview-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 4px;
}

.preview-stat-item {
    flex: 1 1 110px;
    min-width: 100px;
    max-width: 180px;
    padding: 10px 12px;
    background: #f5f7fa;
    border-radius: 6px;
    border: 1px solid #ebeef5;
    box-sizing: border-box;
}

.preview-stat-item.preview-stat-batch {
    flex: 1.6 1 200px;
    max-width: 280px;
}

.preview-stat-item.preview-stat-push {
    flex: 1.4 1 160px;
    max-width: 240px;
}

.preview-stat-item.is-error {
    background: #fef0f0;
    border-color: #fde2e2;
}

.preview-stat-item.is-error .preview-stat-value {
    color: #f56c6c;
}

.preview-stat-label {
    display: block;
    margin-bottom: 4px;
    color: #909399;
    font-size: 12px;
    line-height: 1.2;
}

.preview-stat-value {
    display: block;
    color: #303133;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.35;
    word-break: break-all;
}

.error-collapse {
    margin-top: 12px;
    border: none;
}

.error-collapse >>> .el-collapse-item__header {
    height: 36px;
    line-height: 36px;
    color: #606266;
    border-bottom: none;
}

.error-collapse >>> .el-collapse-item__wrap {
    border-bottom: none;
}

.preview-table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
    font-size: 13px;
    color: #606266;
}

.preview-actions {
    margin-top: 16px;
    text-align: right;
}

.preview-actions .el-button + .el-button {
    margin-left: 8px;
}

.pagination-container {
    margin-top: 12px;
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
}

.detail-summary {
    margin: 0 0 12px;
    color: #606266;
}

.student-eval-import >>> .preview-row-invalid > td {
    background: #fef0f0 !important;
}
</style>
